// src/components/CloudTTSPage.js

import React, { useState, useEffect } from 'react';
import { auth } from '../firebaseConfig'; // Import auth for getting ID token

function CloudTTSPage({ userId }) {
  const [text, setText] = useState('');
  const [languageCode, setLanguageCode] = useState('en-US'); // Default to a common language
  const [voiceName, setVoiceName] = useState('');
  const [ssmlGender, setSsmlGender] = useState('FEMALE');
  const [pitch, setPitch] = useState(0);
  const [speakingRate, setSpeakingRate] = useState(1);
  const [audioEncoding, setAudioEncoding] = useState('MP3');
  const [audioUrl, setAudioUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [error, setError] = useState('');
  const [costEstimate, setCostEstimate] = useState('');

  const [groupedVoices, setGroupedVoices] = useState({}); // Stores voices grouped by language
  const [availableGenders, setAvailableGenders] = useState(['FEMALE', 'MALE', 'NEUTRAL']); // Based on selected voice's gender

  // --- Fetch voices on component mount ---
  useEffect(() => {
    const fetchVoices = async () => {
      setVoicesLoading(true);
      setError('');
      try {
        const response = await fetch('/api/getCloudTtsVoices', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch voices.');
        }

        const data = await response.json();
        setGroupedVoices(data);

        // Set initial language and voice
        if (Object.keys(data).length > 0) {
          const defaultLang = 'it-IT'; // Prefer Italian if available
          const preferredLang = data[defaultLang] ? defaultLang : Object.keys(data)[0];
          setLanguageCode(preferredLang);

          const voicesForPreferredLang = data[preferredLang];
          if (voicesForPreferredLang && voicesForPreferredLang.length > 0) {
            const defaultVoice = voicesForPreferredLang.find(v => v.name === 'it-IT-Wavenet-D') || voicesForPreferredLang[0];
            setVoiceName(defaultVoice.name);
            setSsmlGender(defaultVoice.gender);
            setAvailableGenders(getGendersForVoice(defaultVoice.gender));
          }
        }
      } catch (err) {
        console.error("Error fetching voices:", err);
        setError(`Error loading voices: ${err.message}`);
      } finally {
        setVoicesLoading(false);
      }
    };

    if (userId) { // Fetch only if user is logged in
      fetchVoices();
    }
  }, [userId]);

  // --- Update available genders when voice name changes ---
  useEffect(() => {
    if (languageCode && voiceName && groupedVoices[languageCode]) {
      const selectedVoice = groupedVoices[languageCode].find(v => v.name === voiceName);
      if (selectedVoice) {
        setAvailableGenders(getGendersForVoice(selectedVoice.gender));
        // Reset ssmlGender if current is not available for the new voice
        if (!getGendersForVoice(selectedVoice.gender).includes(ssmlGender)) {
          setSsmlGender(selectedVoice.gender);
        }
      }
    }
  }, [voiceName, languageCode, groupedVoices, ssmlGender]);


  const getGendersForVoice = (voiceGender) => {
    // Basic logic to show gender options. Cloud TTS API returns a specific gender for each voice.
    // We can allow override, but it won't change the underlying voice's inherent gender.
    // For simplicity, we just display the voice's reported gender and standard options.
    return [voiceGender, 'FEMALE', 'MALE', 'NEUTRAL'].filter((value, index, self) => self.indexOf(value) === index);
  };


  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setAudioUrl('');
    setCostEstimate('');

    try {
      const response = await fetch('/api/cloud-tts', { // Call Firebase Function
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          text,
          languageCode,
          voiceName,
          ssmlGender,
          pitch,
          speakingRate,
          audioEncoding,
          userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate audio.');
      }

      const data = await response.json();
      setAudioUrl(data.audioUrl);
      setCostEstimate(`Estimated cost for this generation: $${data.estimatedCost.toFixed(6)} (Free tier: 1M chars for WaveNet/Neural2, 4M for Standard. Current chars: ${text.length})`);
    } catch (err) {
      console.error("Cloud TTS generation error:", err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const currentVoices = groupedVoices[languageCode] || [];

  return (
    <div className="p-6 bg-white rounded-xl shadow-2xl">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">GCP Cloud Text-to-Speech</h2>

      {voicesLoading ? (
        <div className="text-center text-gray-600 text-lg my-8">Loading available voices...</div>
      ) : error && !text ? ( // Only show voice loading error if no text is present
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mt-6" role="alert">
          <p className="font-bold">Error!</p>
          <p className="text-sm">{error}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label htmlFor="text" className="block text-gray-700 text-sm font-semibold mb-2">
                Text to Convert:
              </label>
              <textarea
                id="text"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 resize-y"
                rows="6"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter text here..."
              ></textarea>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="languageCode" className="block text-gray-700 text-sm font-semibold mb-2">
                  Language Code:
                </label>
                <select
                  id="languageCode"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={languageCode}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    setLanguageCode(newLang);
                    const voicesForNewLang = groupedVoices[newLang];
                    if (voicesForNewLang && voicesForNewLang.length > 0) {
                      setVoiceName(voicesForNewLang[0].name); // Select first voice for new lang
                      setSsmlGender(voicesForNewLang[0].gender);
                    } else {
                      setVoiceName('');
                      setSsmlGender('FEMALE'); // Default if no voices
                    }
                  }}
                  disabled={voicesLoading}
                >
                  {Object.keys(groupedVoices).sort().map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="voiceName" className="block text-gray-700 text-sm font-semibold mb-2">
                  Voice Name:
                </label>
                <select
                  id="voiceName"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={voiceName}
                  onChange={(e) => {
                    const selectedVoice = currentVoices.find(v => v.name === e.target.value);
                    if (selectedVoice) {
                      setVoiceName(selectedVoice.name);
                      setSsmlGender(selectedVoice.gender); // Update gender based on chosen voice
                    }
                  }}
                  disabled={voicesLoading || currentVoices.length === 0}
                >
                  {currentVoices.length > 0 ? (
                    currentVoices.map((voice) => (
                      <option key={voice.name} value={voice.name}>
                        {voice.name} (Gender: {voice.gender || 'UNKNOWN'})
                      </option>
                    ))
                  ) : (
                    <option value="">No voices available for this language</option>
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="ssmlGender" className="block text-gray-700 text-sm font-semibold mb-2">
                  SSML Gender:
                </label>
                <select
                  id="ssmlGender"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={ssmlGender}
                  onChange={(e) => setSsmlGender(e.target.value)}
                  disabled={voicesLoading || currentVoices.length === 0}
                >
                  {availableGenders.map(gender => (
                    <option key={gender} value={gender}>{gender}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="pitch" className="block text-gray-700 text-sm font-semibold mb-2">
                  Pitch ({pitch}):
                </label>
                <input
                  type="range"
                  id="pitch"
                  min="-20"
                  max="20"
                  value={pitch}
                  onChange={(e) => setPitch(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
              </div>

              <div>
                <label htmlFor="speakingRate" className="block text-gray-700 text-sm font-semibold mb-2">
                  Speaking Rate ({speakingRate}):
                </label>
                <input
                  type="range"
                  id="speakingRate"
                  min="0.25"
                  max="4"
                  step="0.05"
                  value={speakingRate}
                  onChange={(e) => setSpeakingRate(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
              </div>

              <div>
                <label htmlFor="audioEncoding" className="block text-gray-700 text-sm font-semibold mb-2">
                  Audio Encoding:
                </label>
                <select
                  id="audioEncoding"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  value={audioEncoding}
                  onChange={(e) => setAudioEncoding(e.target.value)}
                >
                  <option value="MP3">MP3</option>
                  <option value="LINEAR16">LINEAR16 (WAV)</option>
                  <option value="OGG_OPUS">OGG_OPUS</option>
                </select>
              </div>
            </div>
          </div>

          <button
            onClick={handleGenerate}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !text.trim() || voicesLoading || currentVoices.length === 0}
          >
            {loading ? 'Generating...' : 'Generate Audio (GCP Cloud TTS)'}
          </button>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mt-6" role="alert">
              <p className="font-bold">Error!</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {audioUrl && (
            <div className="mt-6 p-6 bg-green-50 border border-green-200 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-green-800 mb-3">Generated Audio:</h3>
              <audio controls src={audioUrl} className="w-full">
                Your browser does not support the audio element.
              </audio>
              <p className="text-sm text-gray-600 mt-3">{costEstimate}</p>
              <p className="text-sm text-gray-600 mt-1">Audio saved to "My Files" page.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CloudTTSPage;